import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, MapPin, Users, Award, Zap, CheckCircle2,
  Building2, TrendingUp, Clock, Globe, Shield, Code, Phone, Mail
} from 'lucide-react';

const AIConsultingAhmedabad = () => {
  const whyAhmedabad = [
    {
      icon: Building2,
      title: 'Ahmedabad Headquarters',
      description: 'Established presence at Sun Gravitas, Shyamal Cross Road - Gujarat\'s premier AI innovation hub',
      metric: 'Since 2025'
    },
    {
      icon: Users,
      title: 'Local AI Talent',
      description: '50+ senior AI engineers based in Ahmedabad with global experience',
      metric: '50+ engineers'
    },
    {
      icon: Clock,
      title: 'Rapid Deployment',
      description: 'Face-to-face collaboration for Ahmedabad clients ensures 3-8 week delivery',
      metric: '3-8 weeks'
    },
    {
      icon: Award,
      title: 'Gujarat Leader',
      description: 'Most trusted AI consulting company in Gujarat with 99% satisfaction',
      metric: '#1 in Gujarat'
    },
    {
      icon: TrendingUp,
      title: 'Cost Advantage',
      description: 'Ahmedabad-based pricing with Silicon Valley quality standards',
      metric: '60% savings'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'From our Ahmedabad office, we serve clients across USA, UK, Israel, Caribbean',
      metric: '200+ projects'
    }
  ];

  const ahmedabadIndustries = [
    {
      name: 'Pharmaceutical & Healthcare',
      icon: Shield,
      description: 'AI solutions for Ahmedabad\'s thriving pharma sector',
      clients: 'Zydus, Torrent, Healthcare providers',
      projects: ['Drug discovery AI', 'Clinical trial optimization', 'Patient diagnosis systems', 'Medical imaging analysis']
    },
    {
      name: 'Textiles & Manufacturing',
      icon: Building2,
      description: 'Computer vision and predictive AI for Gujarat\'s textile industry',
      clients: 'Textile mills, Garment manufacturers',
      projects: ['Quality inspection AI', 'Demand forecasting', 'Inventory optimization', 'Defect detection']
    },
    {
      name: 'Finance & Banking',
      icon: TrendingUp,
      description: 'AI for Ahmedabad\'s growing fintech and banking sector',
      clients: 'Banks, NBFCs, Fintech startups',
      projects: ['Fraud detection', 'Credit scoring', 'Risk assessment', 'Robo-advisory']
    },
    {
      name: 'Real Estate & Construction',
      icon: Building2,
      description: 'Predictive analytics for Ahmedabad\'s booming real estate market',
      clients: 'Developers, Property consultants',
      projects: ['Price prediction', 'Demand forecasting', 'Site selection AI', 'Investment analysis']
    },
    {
      name: 'Retail & E-commerce',
      icon: Code,
      description: 'AI-powered personalization for Ahmedabad retailers',
      clients: 'Retail chains, E-commerce platforms',
      projects: ['Recommendation engines', 'Inventory optimization', 'Customer segmentation', 'Dynamic pricing']
    },
    {
      name: 'Education & EdTech',
      icon: Users,
      description: 'AI solutions for Ahmedabad\'s education institutions',
      clients: 'Universities, EdTech startups, Coaching centers',
      projects: ['Personalized learning', 'Student performance prediction', 'Automated grading', 'Chatbots']
    }
  ];

  const localServices = [
    'On-site AI Consulting in Ahmedabad',
    'Face-to-face Strategy Workshops',
    'Local Team Integration',
    'Ahmedabad-based Project Management',
    'Same-day Response for Local Clients',
    'Regular Progress Reviews at Your Office',
    'Local Language Support (Gujarati/Hindi/English)',
    'Ahmedabad Timezone Advantage'
  ];

  const successMetrics = [
    {
      metric: '100+',
      label: 'Gujarat Projects',
      description: 'Across Ahmedabad, Surat, Vadodara'
    },
    {
      metric: '99%',
      label: 'Satisfaction',
      description: 'Highest in Gujarat'
    },
    {
      metric: '3-8',
      label: 'Weeks Deploy',
      description: 'Faster than Bangalore/Mumbai firms'
    },
    {
      metric: '50+',
      label: 'Local Team',
      description: 'Based in Ahmedabad'
    }
  ];

  return (
    <>
      <Helmet>
        <title>AI Consulting Company Ahmedabad | Machine Learning Services Gujarat | Agility</title>
        <meta
          name="description"
          content="Leading AI consulting company in Ahmedabad, Gujarat. 100+ successful AI implementations. Office at Sun Gravitas, Shyamal Cross Road. 3-8 week deployment. Expert AI consultants for pharma, textile, finance, retail. 99% satisfaction."
        />
        <meta
          name="keywords"
          content="AI consulting company Ahmedabad, AI consulting services Gujarat, machine learning Ahmedabad, AI development company Ahmedabad, AI consultants Ahmedabad, artificial intelligence consulting Gujarat, ML consulting Ahmedabad, AI services Ahmedabad"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-company-ahmedabad" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting Company Ahmedabad | Machine Learning Services Gujarat" />
        <meta property="og:description" content="Leading AI consulting company in Ahmedabad. Office at Sun Gravitas, Shyamal Cross Road. 100+ successful projects. 3-8 week deployment." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-company-ahmedabad" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />

        {/* Schema.org LocalBusiness markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Agility - AI Consulting Company Ahmedabad",
            "description": "Leading AI consulting and machine learning services in Ahmedabad, Gujarat. Expert AI solutions for pharma, textile, finance, retail industries.",
            "url": "https://www.agilitytech.ai",
            "telephone": "+91-7041402034",
            "email": "hello@agilitytech.ai",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "14th floor, Sun Gravitas, Shyamal Cross Road, Rajmani Society",
              "addressLocality": "Ahmedabad",
              "addressRegion": "Gujarat",
              "postalCode": "380015",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 23.0225,
              "longitude": 72.5714
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "Ahmedabad"
              },
              {
                "@type": "State",
                "name": "Gujarat"
              }
            ],
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            },
            "serviceType": "AI Consulting and Machine Learning Services",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.95",
              "reviewCount": "100",
              "bestRating": "5"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
              <MapPin className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">Sun Gravitas, Shyamal Cross Road, Ahmedabad</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              <span className="text-teal-400">Ahmedabad's</span> Leading AI Consulting Company
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
              Transform your Gujarat business with production-ready AI systems deployed in 3-8 weeks.
              100+ successful implementations across pharma, textile, finance, and retail.
              99% client satisfaction.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
              >
                Schedule On-site Consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:+917041402034"
                className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all"
              >
                <Phone className="w-5 h-5" />
                +91-7041402034
              </a>
            </div>

            {/* Success Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {successMetrics.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-teal-400 mb-2">{item.metric}</div>
                  <div className="text-white font-semibold mb-1">{item.label}</div>
                  <div className="text-gray-400 text-sm">{item.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Ahmedabad Office */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Our <span className="text-teal-600">Ahmedabad Office</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Local presence with global expertise - the best of both worlds for Gujarat businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyAhmedabad.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <div className="text-3xl font-bold text-teal-600 mb-2">{item.metric}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ahmedabad Address Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Visit Our <span className="text-teal-600">Ahmedabad Office</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Meet our team face-to-face at Gujarat's premier business location
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Address</div>
                    <div className="text-gray-600">
                      14th floor, Sun Gravitas<br />
                      Shyamal Cross Road, Rajmani Society<br />
                      Ahmedabad, Gujarat 380015
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Phone</div>
                    <a href="tel:+917041402034" className="text-teal-600 hover:text-teal-700">
                      +91-7041402034
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Email</div>
                    <a href="mailto:hello@agilitytech.ai" className="text-teal-600 hover:text-teal-700">
                      hello@agilitytech.ai
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Office Hours</div>
                    <div className="text-gray-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: By Appointment
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
                >
                  Schedule Office Visit
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-xl">
              <div className="aspect-video bg-gray-200 rounded-lg mb-6 flex items-center justify-center">
                <MapPin className="w-16 h-16 text-teal-600" />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-gray-600">Nearest Metro</span>
                  <span className="font-semibold text-gray-900">Shyamal (500m)</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-gray-600">From Airport</span>
                  <span className="font-semibold text-gray-900">20 min drive</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <span className="text-gray-600">From Railway Station</span>
                  <span className="font-semibold text-gray-900">15 min drive</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Parking</span>
                  <span className="font-semibold text-gray-900">Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries in Ahmedabad */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              AI Solutions for <span className="text-teal-600">Ahmedabad Industries</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-specific AI implementations for Gujarat's leading sectors
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {ahmedabadIndustries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200">
                  <div className="flex items-start gap-4 mb-6">
                    <Icon className="w-10 h-10 text-teal-600 flex-shrink-0" />
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{industry.name}</h3>
                      <p className="text-gray-600 mb-2">{industry.description}</p>
                      <div className="text-sm text-teal-600 font-medium">{industry.clients}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-3">KEY PROJECTS</div>
                    <ul className="grid grid-cols-2 gap-2">
                      {industry.projects.map((project, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{project}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Local Services */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-teal-400">Premium Services</span> for Ahmedabad Clients
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Personalized AI consulting with on-site support for Gujarat businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {localServices.map((service, index) => (
              <div key={index} className="fade-in-up flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-teal-500/50 transition-all">
                <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0" />
                <span className="text-white font-medium">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Ahmedabad Business with AI?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Visit our Shyamal Cross Road office or schedule an on-site consultation.
            Get a custom AI roadmap in 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+917041402034"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all text-lg"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>
          <p className="mt-6 text-teal-50">
            Email us at <span className="font-semibold">hello@agilitytech.ai</span>
          </p>
        </div>
      </section>
    </>
  );
};

export default AIConsultingAhmedabad;
